import React from "react";
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthProvider } from "./AuthProvider";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import TimesheetEntry from "./pages/TimesheetEntry";
import ProjectFinancials from "./pages/ProjectFinancials";
import UserManagement from "./pages/UserManagement";
import ApprovalQueue from "./pages/ApprovalQueue";
import ApprovalDetail from "./pages/ApprovalDetail";
import SystemConfig from "./pages/SystemConfig";
import ZohoSync from "./pages/ZohoSync";
import CommissionHours from "./pages/CommissionHours";
import { ProjectInfoMaster, ProjectInfoDetail } from "./pages/ProjectInfo";
import TimesheetValidation from "./pages/TimesheetValidation";
import DataQualityReport from "./pages/DataQualityReport";
import TimesheetTemplates from "./pages/TimesheetTemplates";
import InvoiceAutomation from "./pages/InvoiceAutomation";
import { useUserProfile } from "./hooks/useUserProfile";

const AppContent: React.FC = () => {
    const { roles, isSubcontractor, loading, error } = useUserProfile();

    if (loading) return (
        <div style={{ display: "flex", alignItems: "center",
            justifyContent: "center", height: "100vh",
            color: "#5C5D5E", fontSize: "16px" }}>
            Loading...
        </div>
    );

    if (error) return (
        <div style={{ display: "flex", alignItems: "center",
            justifyContent: "center", height: "100vh",
            color: "red", fontSize: "16px" }}>
            {error}
        </div>
    );

    return (
        <Layout roles={roles} isSubcontractor={isSubcontractor}>
            <Routes>
                <Route path="/"                    element={<Home />} />
                <Route path="/timesheets"          element={<TimesheetEntry />} />
                <Route path="/timesheets/templates" element={<TimesheetTemplates />} />
                <Route path="/approvals"           element={<ApprovalQueue />} />
                <Route path="/approvals/project/:projectId" element={<ApprovalDetail />} />
                <Route path="/timesheets/validation"        element={<TimesheetValidation />} />
                <Route path="/reports/financials"  element={<ProjectFinancials />} />
                <Route path="/reports/project-info" element={<ProjectInfoMaster />} />
                <Route path="/reports/project-info/:projectId" element={<ProjectInfoDetail />} />
                <Route path="/compensation"        element={<Navigate to="/reports/commission" />} />
                <Route path="/admin/users"         element={<UserManagement />} />
                <Route path="/admin/zoho-sync"     element={<ZohoSync />} />
                <Route path="/admin/config"        element={<SystemConfig />} />
                <Route path="/reports/commission" element={<CommissionHours />} />
                <Route path="/reports/data-quality" element={<DataQualityReport />} />
                <Route path="/invoicing" element={<InvoiceAutomation />} />
                <Route path="*"                    element={<Navigate to="/" />} />
            </Routes>
        </Layout>
    );
};

const RootLayout: React.FC = () => (
    <>
        <AuthenticatedTemplate>
            <Outlet />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
            <Login />
        </UnauthenticatedTemplate>
    </>
);

const router = createBrowserRouter([
    {
        path: "/*",
        element: <RootLayout />,
        children: [
            { path: "*", element: <AppContent /> },
        ],
    },
]);

const App: React.FC = () => (
    <AuthProvider>
        <RouterProvider router={router} />
    </AuthProvider>
);

export default App;
